/**
 * Collaborative Editors Component
 * 
 * Displays real-time information about users currently editing rooms
 * in the job paste screen. Shows total editors and room-specific editors.
 */

import React, { useState } from 'react';
import { useYJSCollaboration } from 'hooks/useYJSCollaboration';
import { Users, Edit3, Circle, ChevronDown, ChevronUp } from 'lucide-react';
import classes from './CollaborativeEditors.module.scss';

interface CollaborativeEditorsProps {
  className?: string;
}

const CollaborativeEditors: React.FC<CollaborativeEditorsProps> = ({ className }) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  
  const {
    isConnected,
    allEditors,
    totalEditors,
    getRoomEditorCount,
    getRoomEditorNames,
    getFieldEditors,
    isFieldBeingEdited
  } = useYJSCollaboration();

  // Group editors by room
  const editorsByRoom = allEditors.reduce((acc, editor) => {
    if (editor.roomName) { // Only group if roomName is present
      if (!acc[editor.roomName]) {
        acc[editor.roomName] = [];
      }
      acc[editor.roomName].push(editor);
    }
    return acc;
  }, {} as Record<string, typeof allEditors>);

  // Group editors by field for each room
  const editorsByField = allEditors.reduce((acc, editor) => {
    if (editor.roomName && editor.currentField) {
      const key = `${editor.roomName}-${editor.currentField.fieldName}`;
      if (!acc[key]) {
        acc[key] = {
          roomName: editor.roomName,
          fieldName: editor.currentField.fieldName,
          fieldType: editor.currentField.fieldType,
          editors: []
        };
      }
      acc[key].editors.push(editor);
    }
    return acc;
  }, {} as Record<string, { roomName: string; fieldName: string; fieldType: string; editors: typeof allEditors }>);

  return (
    <div className={`${classes.container} ${className || ''}`}>
      {/* Header with collapse toggle */}
      <div className={classes.header} onClick={() => setIsCollapsed(!isCollapsed)}>
        <div className={classes.headerLeft}>
          <Users className={classes.headerIcon} size={16} />
          <span className={classes.headerTitle}>Collaborative Editors</span>
          <span className={classes.editorCount}>({totalEditors})</span>
        </div>
        <div className={classes.headerRight}>
          <Circle 
            className={`${classes.statusIndicator} ${isConnected ? classes.connected : classes.disconnected}`}
            size={8}
          />
          {isCollapsed ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
        </div>
      </div>

      {/* Collapsible Content */}
      {!isCollapsed && (
        <div className={classes.content}>
          {/* Connection Status */}
          <div className={classes.connectionStatus}>
            <Circle 
              className={`${classes.statusIndicator} ${isConnected ? classes.connected : classes.disconnected}`}
              size={8}
            />
            <span className={classes.statusText}>
              {isConnected ? 'Connected' : 'Disconnected'}
            </span>
          </div>

          {/* Total Editors Count */}
          <div className={classes.totalEditors}>
            <Users className={classes.icon} size={16} />
            <span className={classes.count}>{totalEditors}</span>
            <span className={classes.label}>
              {totalEditors === 1 ? 'editor' : 'editors'} online
            </span>
          </div>

              {/* Field-level Editors */}
              {Object.keys(editorsByField).length > 0 && (
                <div className={classes.fieldEditors}>
                  <div className={classes.sectionTitle}>Currently Editing Fields:</div>
                  {Object.values(editorsByField).map((fieldGroup) => (
                    <div key={`${fieldGroup.roomName}-${fieldGroup.fieldName}`} className={classes.fieldGroup}>
                      <div className={classes.fieldHeader}>
                        <Edit3 className={classes.fieldIcon} size={14} />
                        <span className={classes.fieldName}>{fieldGroup.fieldName}</span>
                        <span className={classes.fieldType}>({fieldGroup.fieldType})</span>
                        <span className={classes.editorCount}>({fieldGroup.editors.length})</span>
                      </div>
                      <div className={classes.roomInfo}>
                        <span className={classes.roomName}>Room: {fieldGroup.roomName}</span>
                      </div>
                      <div className={classes.editorList}>
                        {fieldGroup.editors.map((editor) => (
                          <div
                            key={editor.userId}
                            className={classes.editorItem}
                            style={{ borderLeftColor: editor.color }}
                          >
                            <div
                              className={classes.editorAvatar}
                              style={{ backgroundColor: editor.color }}
                            >
                              {editor.userName.charAt(0).toUpperCase()}
                            </div>
                            <div className={classes.editorInfo}>
                              <span className={classes.editorName}>{editor.userName}</span>
                              {editor.currentField?.fieldValue && (
                                <span className={classes.fieldValue}>
                                  Value: {String(editor.currentField.fieldValue).substring(0, 50)}
                                  {String(editor.currentField.fieldValue).length > 50 ? '...' : ''}
                                </span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Room-specific Editors */}
              {Object.keys(editorsByRoom).length > 0 && (
                <div className={classes.roomEditors}>
                  <div className={classes.sectionTitle}>Active Editors:</div>
                  {Object.entries(editorsByRoom).map(([roomName, editors]) => (
                    <div key={roomName} className={classes.roomGroup}>
                      <div className={classes.roomHeader}>
                        <Edit3 className={classes.roomIcon} size={14} />
                        <span className={classes.roomName}>{roomName}</span>
                        <span className={classes.editorCount}>({editors.length})</span>
                      </div>
                      <div className={classes.editorList}>
                        {editors.map((editor) => (
                          <div
                            key={editor.userId}
                            className={classes.editorItem}
                            style={{ borderLeftColor: editor.color }}
                          >
                            <div
                              className={classes.editorAvatar}
                              style={{ backgroundColor: editor.color }}
                            >
                              {editor.userName.charAt(0).toUpperCase()}
                            </div>
                            <div className={classes.editorInfo}>
                              <span className={classes.editorName}>{editor.userName}</span>
                              {editor.jobId && (
                                <span className={classes.jobId}>Job: {editor.jobId}</span>
                              )}
                              {editor.currentField && (
                                <span className={classes.currentField}>
                                  Editing: {editor.currentField.fieldName}
                                </span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}

          {/* No Active Editors Message */}
          {totalEditors === 0 && (
            <div className={classes.noEditors}>
              <Users className={classes.noEditorsIcon} size={20} />
              <span>You're the only one here</span>
              <span className={classes.noEditorsSubtext}>Start editing to see your activity</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CollaborativeEditors;
